import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import { SubscriptionFormProps } from '.';
import { useSubscriptionUpdateForCbpt, useVerifyReCaptcha } from '../../hooks';
import { RECAPTCHA_SITE_KEY } from '../../src/env';
import { buildQueryString, extractQueryParams } from '../../utils/queryStringHelper';
import { CustomSpinner } from '../BaseUI';
import { TextField } from '../FormFields';
import Disclosure from '../shared/Stripe/Disclosure';
import styles from './styles.module.scss';

interface PaymentMethodProps
  extends Omit<SubscriptionFormProps, 'userEmail' | 'userFullName' | 'dob' | 'formLocation'> {
  isReferral?: boolean;
  subscriptionId?: string;
}

interface FormValues {
  username: string;
}

const PaymentMethod = ({
  buttonText,
  isReferral,
  isUserCBPT,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer: { currentPrice, frequency, originalPrice, code },
  subscriptionId,
  userNextBillingDate,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: updateSubscriptionForCbpt } = useSubscriptionUpdateForCbpt();
  const { mutateAsync: verifyReCaptcha } = useVerifyReCaptcha();
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);

  const handlePayment = async (): Promise<never | { error: StripeError }> => {
    const queryString = buildQueryString({
      article_id,
      modal,
      code,
      guid: referringSubscriberGuid,
      subscription_id: subscriptionId,
    });

    const returnUrl = `${protocol}//${host}/payment-status?${queryString}`;
    const paymentResponse = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: returnUrl,
      },
      redirect: 'always',
    });

    return { error: paymentResponse.error };
  };

  const handleSubmit = async ({ username }: FormValues) => {
    // Username is a random hidden field to prevent bots from submitting the form, human users will not see this but bots will
    // so if the field is filled out, we know it's a bot and we don't submit the form.
    if (username) {
      return;
    }
    // Send an empty string for reCaptcha token in test environment so it doesn't fail
    const reCaptchaToken = await reCaptchaRef.current.executeAsync();
    reCaptchaRef.current.reset();

    const { success } = await verifyReCaptcha({ recaptcha_token: reCaptchaToken });
    if (!success) {
      return;
    }

    if (isUserCBPT) {
      setIsLoading(true);
      updateSubscriptionForCbpt({
        page: window.location.pathname,
        code,
        source: 'web',
      });
      setIsLoading(true);
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const { error } = await handlePayment();

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik initialValues={{ username: '' }} onSubmit={handleSubmit}>
        {() => (
          <Form className={styles.paymentForm}>
            <div className="tw-mt-24 tw-mb-20 tw-flex tw-items-center">
              <div className="tw-text-primary-red tw-text-body-sm tw-uppercase tw-font-bold tw-font-sans">
                Payment Information
              </div>
              <div className="tw-ml-12 tw-flex-1 tw-bg-primary-red tw-h-2"></div>
            </div>
            <TextField className="tw-hidden" name="username" />
            {!isUserCBPT && (
              <div id="payment-form">
                <PaymentElement
                  onChange={() => setErrorMessage('')}
                  options={{
                    layout: {
                      type: 'tabs',
                      defaultCollapsed: false,
                    },
                  }}
                />
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
              </div>
            )}
            <div className={styles.totalContainer}>
              <div className="tw-flex tw-items-start tw-justify-between">
                <div className={styles.todaysTotal}>
                  {isUserCBPT && userNextBillingDate
                    ? `You'll be charged on ${userNextBillingDate}`
                    : "Today's Total"}
                </div>
                <div className="tw-text-right tw-flex tw-flex-col">
                  <span className={styles.amount}>${currentPrice.toFixed(2)}</span>
                </div>
              </div>
              {originalPrice && frequency && (
                <div className="tw-flex tw-text-gray-500">
                  {frequency} after the offer concludes{' '}
                  <span className="tw-ml-auto">${originalPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
            {isReferral && (
              <div className={styles.chargeNote}>
                By beginning your subscription, we will be sharing your name with{' '}
                {referringSubscriberName}, who has referred you, and letting them know that
                you&apos;ve started enjoying The Information.
              </div>
            )}
            <Disclosure />
            <Button
              disabled={!stripe || isLoading}
              type="submit"
              className={clsx(styles.submitBtn, 'tw-mt-16')}
            >
              {isLoading ? (
                <CustomSpinner
                  color="#fff"
                  size={10}
                  style={{ display: 'flex', justifyContent: 'center' }}
                />
              ) : (
                <>{buttonText}</>
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default PaymentMethod;
